import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function ArchivePage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'client', direction: 'asc' });
  const limit = 50;

  useEffect(() => {
    const fetchArchived = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/bookings', {
          params: {
            isArchived: 'true',
            includeArchived: 'true', // Ensure we get everything marked as archived
            page,
            limit,
          },
        });
        setBookings(data.bookings || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error('Failed to fetch archived bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArchived();
  }, [page]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedBookings = [...bookings].sort((a, b) => {
    let aValue, bValue;
    switch (sortConfig.key) {
      case 'client':
        aValue = (a.clientName || a.new_client_name || '').toLowerCase();
        bValue = (b.clientName || b.new_client_name || '').toLowerCase();
        break;
      case 'publication':
        aValue = (a.mediaName || a.platform_details || '').toLowerCase();
        bValue = (b.mediaName || b.platform_details || '').toLowerCase();
        break;
      case 'position':
        aValue = (a.positionName || '').toLowerCase();
        bValue = (b.positionName || '').toLowerCase();
        break;
      case 'media_type':
        aValue = (a.media_type || '').toLowerCase();
        bValue = (b.media_type || '').toLowerCase();
        break;
      case 'release_dates':
        aValue = a.releaseDates?.[0]?.release_date || '';
        bValue = b.releaseDates?.[0]?.release_date || '';
        break;
      default:
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
    }
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const flattenedBookings = useMemo(() => {
    const rows = [];
    sortedBookings.forEach(booking => {
      if (booking.releaseDates && booking.releaseDates.length > 0) {
        booking.releaseDates.forEach(release => {
          // In Archive page, we only show archived releases
          if (!release.is_archived) return;

          rows.push({
            ...booking,
            displayReleaseDate: release.release_date,
            displayStatusId: release.status_id,
            displayStatusName: release.status_name,
            displayReleaseId: release.id,
            rowKey: `${booking.id}-${release.id}`
          });
        });
      } else {
        // For main bookings without releases, only show if they are archived
        if (!booking.is_archived) return;

        rows.push({
          ...booking,
          displayReleaseDate: null,
          displayStatusId: booking.status_id,
          displayStatusName: booking.status_name,
          displayReleaseId: null,
          rowKey: `${booking.id}-no-release`
        });
      }
    });
    return rows;
  }, [sortedBookings]);

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="page-content">
      <div className="card">
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>Archive</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            Individual booking dates that were released or cancelled more than 24 hours ago
          </p>
        </div>

        <div className="scroll-container">
          <table className="booking-list-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('client')} style={{ cursor: 'pointer' }}>
                  CLIENT {sortConfig.key === 'client' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('publication')} style={{ cursor: 'pointer' }}>
                  PUBLICATION {sortConfig.key === 'publication' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>
                  POSITION {sortConfig.key === 'position' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th onClick={() => handleSort('media_type')} style={{ cursor: 'pointer' }}>
                  MEDIA TYPE {sortConfig.key === 'media_type' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th>STATUS</th>
                <th onClick={() => handleSort('release_dates')} style={{ cursor: 'pointer' }}>
                  RELEASE DATE {sortConfig.key === 'release_dates' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : ''}
                </th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7">Loading archive...</td></tr>
              ) : flattenedBookings.length ? (
                flattenedBookings.map((row) => (
                  <tr key={row.rowKey}>
                    <td>{row.clientName || row.new_client_name || '-'}</td>
                    <td>{row.mediaName || row.platform_details || '-'}</td>
                    <td>{row.positionName || '-'}</td>
                    <td>{row.media_type}</td>
                    <td>
                      <span className={`pill pill-${row.displayStatusName?.toLowerCase() === 'released' ? 'green' : 'red'}`}>
                        {row.displayStatusName}
                      </span>
                    </td>
                    <td>{formatDate(row.displayReleaseDate)}</td>
                    <td className="actions-cell">
                      <button className="action-btn" onClick={() => navigate(`/bookings/view/${row.id}`)}>👁️</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7">No archived bookings found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalCount)} of {totalCount} bookings
            </div>
            <div className="pagination-controls">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="pagination-btn">Previous</button>
              <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="pagination-btn">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
